import { useCallback, useMemo, useRef, useState } from "react";
import { API_PATHS, PLANNING_OPERATION_MESSAGES } from "../../../../../config";
import { apiRequest } from "../../../../../services/api/axios";
import { patchScopeAspects, updateScope } from "../../../../../services/api/planning";
import {
  formatShortMonthDay,
  getCalendarDayCount,
  getInclusiveCalendarDaySpan,
  getInclusiveWorkingDaySpan,
  getShiftedDate,
  isLocalWeekday,
  toLocalCalendarDay,
  toLocalDateKey,
} from "../../../../../utils/datetime/core";
import {
  mapAspectFromApi,
  mapBlockFromApi,
} from "../../model/scheduleApiMappers";
import {
  blockDateSpanHasLockedDay,
  getAspectBlockId,
} from "../../model/scheduleBlockSelectors";
import { isDayWithinJobActiveRange } from "../../model/scheduleJobDateRange";
import { sameScheduleBlockId } from "../../model/scheduleIds";
import { materializeAspectDaysForBlock } from "../../model/scheduleMaterializeAspectDays";
import {
  buildFilteredWorkerIdsAfterConflictStrip,
  getWorkerConflictSummariesByDate,
} from "../../../shared/planningWorkerConflicts";
import {
  buildMapByNumericId,
  pickItemsByNumericIdsInOrder,
} from "../../../../../utils/collections/numericId";

/**
 * Manage scope create modal, optimistic block creation, and block copy/paste flows.
 *
 * @param {Object} params
 * @param {Array<Object>} params.visibleJobs Jobs in the visible timeline window.
 * @param {Array<Object>} params.workScopes Work-scope catalog.
 * @param {import('react').MutableRefObject<Array<Object>>} params.allJobsRef Full job list ref.
 * @param {import('react').MutableRefObject<Array<Object>>} params.workScopeAspectsRef Aspect list ref.
 * @param {import('react').MutableRefObject<Array<Object>>} params.blockDraftWorkers Draft crew during create.
 * @param {Function} params.setBlocks
 * @param {Function} params.setWorkScopeAspects
 * @param {Function} params.runPlanningOperation
 * @param {(date: Date, jobRaw: Object) => Date} params.clampLocalDateToJobRange
 * @param {Record<string, boolean>} [params.scheduleDayLocks] Dispatch day lock keys for the visible schedule range.
 * @param {(opts: { conflictSummaries: Array<Object>, onConfirm: () => void }) => void} [params.requestWorkerConflictConfirmation]
 * @param {Array<Object>|undefined} [params.workers] Crew catalog.
 * @param {(message: string, type?: 'success'|'info'|'error') => void} [params.notify] Planning toast (`PlanningContext.notify`).
 * @param {(workerIds: Array<number|string>, context?: Object) => Promise<void>} [params.applyAspectWorkersToAllDays] Existing default-crew flow.
 * @returns {{
 *   createModalOpen: boolean,
 *   createAnchorLabel: string,
 *   createDefaultWorkingDaysOnly: boolean,
 *   handleCloseCreateModal: () => void,
 *   handleCellClick: Function,
 *   handleCreateScope: Function,
 *   copiedScopeBlock: import('react').MutableRefObject<Object|null>,
 *   clipboardRevision: number,
 *   copyScopeBlock: Function,
 *   pasteScopeBlockAtCell: Function,
 * }}
 */
export function useScheduleCreatePasteActions({
  visibleJobs,
  workScopes,
  allJobsRef,
  workScopeAspectsRef,
  blockDraftWorkers,
  setBlocks,
  setWorkScopeAspects,
  runPlanningOperation,
  clampLocalDateToJobRange,
  scheduleDayLocks = {},
  requestWorkerConflictConfirmation,
  workers,
  notify,
  applyAspectWorkersToAllDays,
}) {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const createIntentRef = useRef(null);
  /** @type {React.MutableRefObject<{ block: Object, aspectTemplates: Array<{ skipped: boolean, worker_assignment: Array<Object> }> }|null>} */
  const copiedScopeBlock = useRef(null);
  const [clipboardRevision, setClipboardRevision] = useState(0);

  /** Close create-scope modal and clear pending create intent. */
  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    createIntentRef.current = null;
  }, []);

  /** Start scope creation flow from a clicked schedule cell when target row/day is valid. */
  const handleCellClick = useCallback(
    ({ row, day }) => {
      if (row.rowType !== "project" && row.rowType !== "group") {
        return;
      }
      const job = visibleJobs.find((p) => p.id === row.projectId);
      if (!job || !isDayWithinJobActiveRange(toLocalDateKey(day), job)) {
        return;
      }
      createIntentRef.current = { row, anchorDate: day };
      setCreateModalOpen(true);
    },
    [visibleJobs]
  );

  /** Create a new scope block from modal input with optimistic add and server reconciliation. */
  const handleCreateScope = useCallback(
    ({ workScopeId, days, workingDaysOnly }) => {
      const intent = createIntentRef.current;
      if (!intent) {
        return;
      }
      const { row, anchorDate } = intent;
      const catalogRow = workScopes.find((w) => String(w.id) === String(workScopeId));
      if (!catalogRow) {
        return;
      }
      const project = visibleJobs.find((p) => p.id === row.projectId);
      if (!project) {
        return;
      }
      const jobRaw = allJobsRef.current.find((j) => j.id === project.id);
      let { startDate, endDate } = workingDaysOnly
        ? getInclusiveWorkingDaySpan(anchorDate, days)
        : getInclusiveCalendarDaySpan(anchorDate, days);
      if (jobRaw) {
        startDate = clampLocalDateToJobRange(startDate, jobRaw);
        endDate = clampLocalDateToJobRange(endDate, jobRaw);
        if (startDate.getTime() > endDate.getTime()) {
          endDate = startDate;
        }
      }
      startDate = toLocalCalendarDay(startDate);
      endDate = toLocalCalendarDay(endDate);

      const blockId = `block-local-${Date.now()}`;
      const newBlock = {
        id: blockId,
        job: {
          id: project.id,
          number: project.projectNumber,
          description: project.name,
        },
        scheduleGroup:
          row.rowType === "group" && row.groupId ? { id: row.groupId } : null,
        scope: {
          id: catalogRow.id,
          name: catalogRow.name,
        },
        startDate,
        endDate,
        workingDaysOnly: Boolean(workingDaysOnly),
        notes: "",
        worker_assignment: blockDraftWorkers.current ?? [],
      };

      const newDays = materializeAspectDaysForBlock(newBlock);
      setBlocks((prev) => [...prev, newBlock]);
      setWorkScopeAspects((prev) => [...prev, ...newDays]);
      handleCloseCreateModal();

      const scheduleGroupId =
        row.rowType === "group" && row.groupId != null ? row.groupId : null;
      const draftWorkerIds = (blockDraftWorkers.current ?? [])
        .map((w) => w.id)
        .filter((id) => id != null && id !== "");
      const payload = {
        job_id: project.id,
        work_scope_id: catalogRow.id,
        schedule_group_id: scheduleGroupId,
        start_date: toLocalDateKey(startDate),
        end_date: toLocalDateKey(endDate),
        working_days_only: Boolean(workingDaysOnly),
        worker_ids: [],
      };
      void runPlanningOperation({
        module: "schedule",
        operation: "createScheduleBlock",
        resources: ["schedule.blocks", "schedule.aspects"],
        apiCall: () => apiRequest(API_PATHS.SCHEDULE.BLOCKS, payload, { method: "POST" }),
      })
        .then((raw) => {
          const mapped = mapBlockFromApi(raw);
          const mappedAspects = (raw.aspects ?? []).map((a) =>
            mapAspectFromApi(a, mapped.id)
          );
          blockDraftWorkers.current = [];
          setBlocks((prev) => [
            ...prev.filter((b) => !sameScheduleBlockId(b.id, blockId)),
            mapped,
          ]);
          setWorkScopeAspects((prev) => [
            ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
            ...mappedAspects,
          ]);
          if (draftWorkerIds.length > 0) {
            void applyAspectWorkersToAllDays?.(draftWorkerIds, {
              block: mapped,
              aspects: [
                ...workScopeAspectsRef.current.filter(
                  (d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)
                ),
                ...mappedAspects,
              ],
            });
          }
        })
        .catch((err) => {
          console.error("Schedule block create failed", err);
          setBlocks((prev) => prev.filter((b) => !sameScheduleBlockId(b.id, blockId)));
          setWorkScopeAspects((prev) =>
            prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId))
          );
        });
    },
    [
      allJobsRef,
      applyAspectWorkersToAllDays,
      blockDraftWorkers,
      clampLocalDateToJobRange,
      handleCloseCreateModal,
      runPlanningOperation,
      setBlocks,
      setWorkScopeAspects,
      visibleJobs,
      workScopeAspectsRef,
      workScopes,
    ]
  );

  /** Copy block + ordered per-day templates for later timeline paste. */
  const copyScopeBlock = useCallback(
    (block) => {
      if (!block?.id) {
        return;
      }
      const templates = workScopeAspectsRef.current
        .filter((a) => sameScheduleBlockId(getAspectBlockId(a), block.id))
        .slice()
        .sort(
          (a, b) =>
            toLocalCalendarDay(a.date).getTime() -
            toLocalCalendarDay(b.date).getTime()
        )
        .map((a) => ({
          skipped: Boolean(a.skipped),
          worker_assignment: Array.isArray(a.worker_assignment)
            ? a.worker_assignment.map((w) => ({ ...w }))
            : [],
        }));
      copiedScopeBlock.current = { block: { ...block }, aspectTemplates: templates };
      setClipboardRevision((n) => n + 1);
    },
    [workScopeAspectsRef]
  );

  /** Paste copied block into a target cell, clone per-day details, then sync against API ids. */
  const pasteScopeBlockAtCell = useCallback(
    ({ row, day }) => {
      const snap = copiedScopeBlock.current;
      if (!snap?.block?.scope?.id || !(day instanceof Date)) {
        return;
      }
      if (row.rowType !== "project" && row.rowType !== "group") {
        return;
      }
      const project = visibleJobs.find((p) => p.id === row.projectId);
      if (!project) {
        return;
      }
      if (!isDayWithinJobActiveRange(toLocalDateKey(day), project)) {
        return;
      }
      const source = snap.block;
      const aspectTemplates = snap.aspectTemplates ?? [];
      const jobRaw = allJobsRef.current.find((j) => j.id === project.id);

      const srcStart = toLocalCalendarDay(source.startDate);
      const srcEnd = toLocalCalendarDay(source.endDate);
      const spanDays = Math.max(1, getCalendarDayCount(srcStart, srcEnd, true, true));

      let startDate = toLocalCalendarDay(day);
      let endDate = toLocalCalendarDay(getShiftedDate(startDate, spanDays - 1));
      if (jobRaw) {
        startDate = clampLocalDateToJobRange(startDate, jobRaw);
        endDate = clampLocalDateToJobRange(endDate, jobRaw);
        if (startDate.getTime() > endDate.getTime()) {
          endDate = startDate;
        }
      }

      const actualDayCount = Math.max(
        1,
        getCalendarDayCount(startDate, endDate, true, true)
      );
      const dayList = [];
      for (let i = 0; i < actualDayCount; i += 1) {
        dayList.push(toLocalCalendarDay(getShiftedDate(startDate, i)));
      }

      const blockId = `block-local-${Date.now()}`;
      const scheduleGroupId =
        row.rowType === "group" && row.groupId != null ? row.groupId : null;
      const workerIdsFromBlock = (source.worker_assignment ?? [])
        .map((w) => w.id)
        .filter((id) => id != null && id !== "");

      const newBlock = {
        id: blockId,
        job: {
          id: project.id,
          number: project.projectNumber,
          description: project.name,
        },
        scheduleGroup:
          row.rowType === "group" && row.groupId ? { id: row.groupId } : null,
        scope: {
          id: source.scope.id,
          name: source.scope.name,
        },
        startDate,
        endDate,
        workingDaysOnly: false,
        notes: source.notes ?? "",
        worker_assignment: (source.worker_assignment ?? []).map((w) => ({ ...w })),
      };

      if (blockDateSpanHasLockedDay(newBlock, scheduleDayLocks)) {
        notify?.(
          PLANNING_OPERATION_MESSAGES.scheduleCannotPasteScopeSpanningLockedDays,
          "error"
        );
        return;
      }

      const newDays = dayList.map((date, i) => {
        const t =
          aspectTemplates[i] ?? aspectTemplates[aspectTemplates.length - 1] ?? null;
        const fallbackWorkers = (newBlock.worker_assignment ?? []).map((w) => ({
          ...w,
        }));
        const worker_assignment =
          t?.worker_assignment?.length > 0 ? t.worker_assignment : fallbackWorkers;
        return {
          id: `${blockId}-day-${toLocalDateKey(date)}`,
          dayPosition: 0,
          workScopeAspectsBlock: { id: blockId },
          date,
          worker_assignment: worker_assignment.map((w) => ({ ...w })),
          skipped: Boolean(t?.skipped),
        };
      });
      const catalogWorkers = workers ?? [];
      const workersById = buildMapByNumericId(catalogWorkers);
      const mergedAspectsForCheck = [...workScopeAspectsRef.current, ...newDays];
      const conflictSummaries = getWorkerConflictSummariesByDate(
        mergedAspectsForCheck,
        dayList.map(toLocalDateKey),
        { workers: catalogWorkers }
      ).filter((summary) => summary.conflictWorkerIds.length > 0);

      const payload = {
        job_id: project.id,
        work_scope_id: source.scope.id,
        schedule_group_id: scheduleGroupId,
        start_date: toLocalDateKey(startDate),
        end_date: toLocalDateKey(endDate),
        working_days_only: false,
        worker_ids: workerIdsFromBlock,
      };

      const runPasteScopeBlock = (summariesForResolution = []) => {
        const summaryByDateKey = new Map(
          summariesForResolution.map((summary) => [summary.dateKey, summary])
        );
        const optimisticDays = newDays.map((row) => {
          const summary = summaryByDateKey.get(toLocalDateKey(row.date));
          if (!summary || summary.conflictWorkerIds.length === 0) {
            return row;
          }
          const filteredWorkerIds = buildFilteredWorkerIdsAfterConflictStrip({
            block: newBlock,
            aspect: row,
            conflictWorkerIds: summary.conflictWorkerIds,
          });
          return {
            ...row,
            worker_assignment: pickItemsByNumericIdsInOrder(
              workersById,
              filteredWorkerIds
            ),
          };
        });
        setBlocks((prev) => [...prev, newBlock]);
        setWorkScopeAspects((prev) => [...prev, ...optimisticDays]);

        void runPlanningOperation({
          module: "schedule",
          operation: "pasteScheduleBlock",
          resources: ["schedule.blocks", "schedule.aspects"],
          apiCall: () => apiRequest(API_PATHS.SCHEDULE.BLOCKS, payload, { method: "POST" }),
        })
          .then(async (raw) => {
            const mapped = mapBlockFromApi(raw);
            const mappedAspects = (raw.aspects ?? []).map((a) =>
              mapAspectFromApi(a, mapped.id)
            );
            const sortedNew = [...mappedAspects].sort(
              (a, b) =>
                toLocalCalendarDay(a.date).getTime() -
                toLocalCalendarDay(b.date).getTime()
            );
            try {
              const workerPatchRows = [];
              await Promise.all(
                sortedNew.map(async (aspect, i) => {
                  const t =
                    aspectTemplates[i] ??
                    aspectTemplates[aspectTemplates.length - 1] ??
                    null;
                  if (!t || !/^\d+$/.test(String(aspect.id))) {
                    return;
                  }
                  const aid = Number(aspect.id);
                  const wantSkipped = Boolean(t.skipped);
                  let wantIdsOrdered = (t.worker_assignment ?? [])
                    .map((w) => w.id)
                    .filter((id) => id != null && id !== "");
                  const summary = summaryByDateKey.get(toLocalDateKey(aspect.date));
                  if (summary && summary.conflictWorkerIds.length > 0) {
                    const desiredAspect = {
                      ...aspect,
                      worker_assignment: pickItemsByNumericIdsInOrder(
                        workersById,
                        wantIdsOrdered
                      ),
                    };
                    wantIdsOrdered = buildFilteredWorkerIdsAfterConflictStrip({
                      block: mapped,
                      aspect: desiredAspect,
                      conflictWorkerIds: summary.conflictWorkerIds,
                    });
                  }
                  const wantIdsSorted = wantIdsOrdered
                    .slice()
                    .sort((a, b) => Number(a) - Number(b));
                  const haveIds = (aspect.worker_assignment ?? [])
                    .map((w) => w.id)
                    .filter((id) => id != null)
                    .slice()
                    .sort((a, b) => Number(a) - Number(b));
                  const skippedDiff = wantSkipped !== Boolean(aspect.skipped);
                  const workersDiff =
                    wantIdsSorted.length !== haveIds.length ||
                    wantIdsSorted.some((id, j) => id !== haveIds[j]);
                  if (skippedDiff) {
                    await runPlanningOperation({
                      module: "schedule",
                      operation: "updatePastedScopeSkipped",
                      resources: ["schedule.aspects"],
                      apiCall: () => updateScope(aid, { skipped: wantSkipped }, "schedule"),
                    });
                  }
                  if (workersDiff) {
                    workerPatchRows.push({
                      scopeId: aid,
                      workerIds: wantIdsOrdered,
                    });
                  }
                })
              );
              if (workerPatchRows.length > 0) {
                await runPlanningOperation({
                  module: "schedule",
                  operation: "updatePastedScheduleCrews",
                  resources: ["schedule.aspects"],
                  apiCall: () => patchScopeAspects(workerPatchRows, undefined, "schedule"),
                });
              }
            } catch (err) {
              console.error("Schedule paste aspect sync failed", err);
            }

            const sortedFinal = sortedNew.map((aspect, i) => {
              const t =
                aspectTemplates[i] ?? aspectTemplates[aspectTemplates.length - 1] ?? null;
              if (!t) {
                return aspect;
              }
              let worker_assignment = (t.worker_assignment ?? []).map((w) => ({ ...w }));
              const summary = summaryByDateKey.get(toLocalDateKey(aspect.date));
              if (summary && summary.conflictWorkerIds.length > 0) {
                const filteredWorkerIds = buildFilteredWorkerIdsAfterConflictStrip({
                  block: mapped,
                  aspect: { ...aspect, worker_assignment },
                  conflictWorkerIds: summary.conflictWorkerIds,
                });
                worker_assignment = pickItemsByNumericIdsInOrder(
                  workersById,
                  filteredWorkerIds
                );
              }
              return {
                ...aspect,
                skipped: Boolean(t.skipped),
                worker_assignment,
              };
            });

            setBlocks((prev) => [
              ...prev.filter((b) => !sameScheduleBlockId(b.id, blockId)),
              mapped,
            ]);
            setWorkScopeAspects((prev) => [
              ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
              ...sortedFinal,
            ]);
          })
          .catch((err) => {
            console.error("Schedule block paste (create) failed", err);
            setBlocks((prev) => prev.filter((b) => !sameScheduleBlockId(b.id, blockId)));
            setWorkScopeAspects((prev) =>
              prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId))
            );
          });
      };
      if (conflictSummaries.length > 0) {
        requestWorkerConflictConfirmation?.({
          conflictSummaries,
          onConfirm: () => runPasteScopeBlock(conflictSummaries),
        });
      } else {
        runPasteScopeBlock();
      }
    },
    [
      allJobsRef,
      clampLocalDateToJobRange,
      notify,
      requestWorkerConflictConfirmation,
      runPlanningOperation,
      scheduleDayLocks,
      setBlocks,
      setWorkScopeAspects,
      visibleJobs,
      workers,
      workScopeAspectsRef,
    ]
  );

  /** Human-readable label shown in create modal for selected row/date anchor. */
  const createAnchorLabel = useMemo(() => {
    if (!createModalOpen) {
      return "";
    }
    const intent = createIntentRef.current;
    if (!intent) {
      return "";
    }
    const { row, anchorDate } = intent;
    return `${row.label} · ${formatShortMonthDay(anchorDate)}`;
  }, [createModalOpen]);

  /** Default for "Working days only" in create modal: on for weekday cells, off for weekend. */
  const createDefaultWorkingDaysOnly = useMemo(() => {
    if (!createModalOpen) {
      return true;
    }
    const intent = createIntentRef.current;
    if (!intent?.anchorDate) {
      return true;
    }
    return isLocalWeekday(intent.anchorDate);
  }, [createModalOpen]);

  return {
    createModalOpen,
    createAnchorLabel,
    createDefaultWorkingDaysOnly,
    handleCloseCreateModal,
    handleCellClick,
    handleCreateScope,
    copiedScopeBlock,
    clipboardRevision,
    copyScopeBlock,
    pasteScopeBlockAtCell,
  };
}

